import styled from 'styled-components';

interface ToastProps {
  variant?: 'success' | 'error' | 'warning' | 'info';
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  isVisible?: boolean;
}

const getToastColors = (variant: string) => {
  switch (variant) {
    case 'success':
      return {
        background: '#10b981',
        text: 'white',
        icon: 'white'
      };
    case 'error':
      return {
        background: '#ef4444',
        text: 'white',
        icon: 'white'
      };
    case 'warning':
      return {
        background: '#f59e0b',
        text: 'white',
        icon: 'white'
      };
    case 'info':
    default:
      return {
        background: '#3b82f6',
        text: 'white',
        icon: 'white'
      };
  }
};

const getToastPosition = (position: string) => {
  switch (position) {
    case 'top-left':
      return { top: '1rem', left: '1rem' };
    case 'top-right':
      return { top: '1rem', right: '1rem' };
    case 'top-center':
      return { top: '1rem', left: '50%', transform: 'translateX(-50%)' };
    case 'bottom-left':
      return { bottom: '1rem', left: '1rem' };
    case 'bottom-right':
      return { bottom: '1rem', right: '1rem' };
    case 'bottom-center':
      return { bottom: '1rem', left: '50%', transform: 'translateX(-50%)' };
    default:
      return { top: '1rem', right: '1rem' };
  }
};

export const ToastContainer = styled.div<ToastProps>`
  position: fixed;
  ${props => {
    const pos = getToastPosition(props.position || 'top-right');
    return Object.entries(pos).map(([key, value]) => `${key}: ${value};`).join(' ');
  }}
  z-index: 1050;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  pointer-events: none;
`;

export const Toast = styled.div<ToastProps>`
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: ${props => getToastColors(props.variant || 'info').background};
  color: ${props => getToastColors(props.variant || 'info').text};
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  min-width: 300px;
  max-width: 400px;
  pointer-events: auto;
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(-100%)'};
  opacity: ${props => props.isVisible ? '1' : '0'};
  transition: all 0.3s ease-in-out;
`;

export const ToastIcon = styled.div<ToastProps>`
  color: ${props => getToastColors(props.variant || 'info').icon};
  font-size: 1.25rem;
  flex-shrink: 0;
`;

export const ToastContent = styled.div`
  flex: 1;
`;

export const ToastTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
`;

export const ToastMessage = styled.p`
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.4;
  opacity: 0.9;
`;

export const ToastCloseButton = styled.button<ToastProps>`
  background: none;
  border: none;
  color: ${props => getToastColors(props.variant || 'info').text};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  font-size: 1.125rem;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus {
    outline: none;
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }
`;

// Specialized toast variants
export const SuccessToast = styled(Toast).attrs({ variant: 'success' })``;
export const ErrorToast = styled(Toast).attrs({ variant: 'error' })``;
export const WarningToast = styled(Toast).attrs({ variant: 'warning' })``;
export const InfoToast = styled(Toast).attrs({ variant: 'info' })``;

export const ToastProgress = styled.div<{ progress?: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  width: ${props => props.progress || 0}%;
  transition: width 0.1s linear;
  border-radius: 0 0 8px 8px;
`;